import React, { useState, useMemo, useCallback } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    TableSortLabel,
    Box,
    Tooltip
} from "@mui/material";
import './ReportingMetricsTable.scss';
import CustomDrawerComponent from "../Utils/CustomDrawerComponent";
import { arrowIcon } from "../DeviceComponent/TableDevice";

const COLUMNS = [
    { id: "clicks_kpi", label: "Clicks", align: "right", showCurrency: false },
    { id: "cost_kpi", label: "Cost", align: "right", showCurrency: true },
    { id: "conversion_kpi", label: "Conv. Rate", align: "right", showCurrency: false },
    { id: "generated_kpi", label: "Generated", align: "right", showCurrency: true },
    { id: "roas_kpi", label: "ROAS", align: "right", showCurrency: false },
];

const CURRENCY_FIELDS = ["cost_kpi", "generated_kpi", "cpc_kpi"];

const ReportingMetricsTable = ({ metrics, errorMessage = null }) => {
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState(null);

    const safeGet = useCallback((data, key) => data?.[key] ?? 0, []);

    const getComparator = useCallback((order, orderBy) => {
        const descendingComparator = (a, b) => {
            let valueA = safeGet(a.data, orderBy);
            let valueB = safeGet(b.data, orderBy);

            if (CURRENCY_FIELDS.includes(orderBy)) {
                valueA = Number(String(valueA).replace(/,/g, ""));
                valueB = Number(String(valueB).replace(/,/g, ""));
            } else if (orderBy === "clicks_kpi" || orderBy === "roas_kpi") {
                valueA = Number(valueA);
                valueB = Number(valueB);
            }
            return valueB < valueA ? -1 : valueB > valueA ? 1 : 0;
        };

        return order === "desc"
            ? descendingComparator
            : (a, b) => -descendingComparator(a, b);
    }, [safeGet]);

    const stableSort = useCallback((array, comparator) => {
        return array
            .map((el, index) => [el, index])
            .sort((a, b) => {
                const order = comparator(a[0], b[0]);
                return order !== 0 ? order : a[1] - b[1];
            })
            .map((el) => el[0]);
    }, []);

    const handleSortRequest = useCallback((property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    }, [order, orderBy]);

    const rows = useMemo(() => [
        { name: "Metasearch Ads", data: metrics?.metasearch_metrics || {} },
        { name: "Destination Ads", data: metrics?.destination_metrics || {} },
        { name: "Search Ads", data: metrics?.search_ads_metrics || {} },
        { name: "PMax", data: metrics?.pmax_metrics || {} },
        { name: "Display Ads", data: metrics?.display_ads_metrics || {} },
        { name: "AI Connect", data: metrics?.ai_connect_metrics || {} },
        { name: "Meta Hotel Ads", data: metrics?.meta_travel_ads_metrics || {} },
    ], [metrics]);

    const sortedRows = useMemo(() => 
        stableSort(rows, getComparator(order, orderBy)),
        [rows, order, orderBy, stableSort, getComparator]
    );

    const isLoading = useMemo(() => Object.keys(metrics).length === 0, [metrics]);

    const renderComparisonTooltip = useCallback((data, columnId, showCurrency) => {
        if (!data?.compare_enabled) return null;

        const comparePercent = safeGet(data, `${columnId}_compare_percent`);
        const compareValue = safeGet(data, `${columnId}_compare`);

        return (
            <Tooltip
                title={`${showCurrency ? metrics?.currency_symbol || '' : ''}${compareValue}`}
                sx={{ margin: "0px !important" }}
            >
                <span
                    style={{
                        color: comparePercent < 0 ? "red" : "green",
                        width: "45px",
                        display: "flex",
                        justifyContent: "space-between"
                    }}
                >
                    {arrowIcon(comparePercent < 0)} {comparePercent}%
                </span>
            </Tooltip>
        );
    }, [metrics?.currency_symbol, safeGet]);

    const MetricCell = useCallback(({ row, columnId, showCurrency = false }) => (
        <TableCell className="table-metasearch-reporting-body" align="right">
            <span className="table-cell-with-comparative">
                {showCurrency && metrics?.currency_symbol}
                {safeGet(row.data, columnId)}
                {renderComparisonTooltip(row.data, columnId, showCurrency)}
            </span>
        </TableCell>
    ), [metrics?.currency_symbol, safeGet, renderComparisonTooltip]);

    const TotalCell = useCallback(({ columnId, showCurrency = false }) => (
        <TableCell align="right" className="table-metasearch-reporting-bottom">
            <span className="table-cell-with-comparative" style={{margin: "0px !important"}}>
                {showCurrency && metrics?.currency_symbol}
                {safeGet(metrics?.total || {}, columnId)}
                {renderComparisonTooltip(metrics?.total, columnId, showCurrency)}
            </span>
        </TableCell>
    ), [metrics?.currency_symbol, metrics?.total, safeGet, renderComparisonTooltip]);

    return (
        <Box
            className="metasearch-reporting-component-table"
            position="relative"
            style={{ overflow: "hidden" }}
        >
            <CustomDrawerComponent
                isLoading={isLoading}
                errorMessage={errorMessage}
            />
            <Box sx={{ paddingInline: "20px" }}>
                <TableContainer
                    component={Paper}
                    className="metasearch-reporting-component-table-container"
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">
                                    <Typography className="table-metasearch-reporting-header title-channel">
                                        Channel
                                    </Typography>
                                </TableCell>
                                {COLUMNS.map((column) => (
                                    <TableCell key={column.id} align={column.align}>
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : "asc"}
                                            onClick={() => handleSortRequest(column.id)}
                                        >
                                            <Typography className="table-metasearch-reporting-header">
                                                {column.label}
                                            </Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRows.map((row) => (
                                <TableRow key={row.name}>
                                    <TableCell className="table-metasearch-reporting-body-left">
                                        {row.name}
                                    </TableCell>
                                    {COLUMNS.map((column) => (
                                        <MetricCell 
                                            key={column.id}
                                            row={row} 
                                            columnId={column.id} 
                                            showCurrency={column.showCurrency}
                                        />
                                    ))}
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell className="table-metasearch-reporting-bottom total">
                                    Total
                                </TableCell>
                                {COLUMNS.map((column) => (
                                    <TotalCell 
                                        key={column.id}
                                        columnId={column.id} 
                                        showCurrency={column.showCurrency}
                                    />
                                ))}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default ReportingMetricsTable;
